import React, { useCallback, useState } from 'react'
import { required as validateRequired } from 'react-admin'
import { useInput } from 'ra-core'

import GenericInput from 'src/components/inputs/GenericInput'
import { validators } from './validators'

const defaultCoordinate = {
  longitude: 0,
  latitude: 0,
  altitude: 0,
}

const defaultValues = {
  boolean: false,
  number: 0,
  date: new Date(),
  'birth-date': new Date(),
  'date-time': new Date(),
  Coordinate: defaultCoordinate,
}

/**
 * @param {import('src/types').TEntityOverrideProp<'edit' | 'create'>} property
 * @param {import('ra-core').Validator[]} defaultValidations
 */
const handleValidation = (property, defaultValidations = []) => {
  const {
    type,
    basedOn,
    reference,
    displayedName,
    hidden,
    readOnly,
    ...restProperty
  } = property

  const res = Object.keys(restProperty).reduce((acc, cur) => {
    const validator = validators[cur]
    if (typeof validator !== 'function') return acc

    acc.push(validator(restProperty[cur]))
    return acc
  }, defaultValidations.slice())

  return res
}

/**
 * @param {Omit<import('src/types').IInputProps<any>, 'onChange' | 'value' | 'touched' | 'error' | 'invalid' | 'initial' | 'onBlur'> & { required?: boolean }} props
 *
 * @returns {JSX.Element}
 */
const ConnectedInput = ({
  property,
  name,
  record = {},
  required,
  classes,
  resource,
  ...rest
}) => {
  const [internalValidationError, setInternalValidationError] =
    useState(undefined)
  const handleDefaultValue = () => {
    if (property?.defaultValue !== undefined) return property.defaultValue

    if (defaultValues[property?.type] !== undefined)
      return defaultValues[property.type]

    return null
  }

  const handleValidationError = useCallback(error => {
    setInternalValidationError(error)
  }, [])

  const {
    input: { onChange, value, onBlur },
    meta: { error, touched, invalid, initial },
  } = useInput({
    defaultValue: handleDefaultValue(),
    allowNull: true,
    source: name,
    required,
    validate: handleValidation(property, [
      () => internalValidationError,
      ...(required ? [validateRequired()] : []),
      ...(Array.isArray(property.validations) ? property.validations : []),
    ]),
    ...rest,
  })

  return (
    <GenericInput
      onChange={onChange}
      onValidationError={handleValidationError}
      onBlur={onBlur}
      value={value}
      property={property}
      required={required}
      name={name}
      record={record}
      classes={classes}
      error={error}
      invalid={invalid}
      touched={touched}
      initialValue={initial}
      resource={resource}
    />
  )
}

export default ConnectedInput
